import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PhoneInput } from '@/components/PhoneInput';
import { OrientationMismatchDialog } from '@/components/OrientationMismatchDialog';
import { PhotoUploadField } from '@/components/PhotoUploadField';
import { type CountryCode } from 'react-phone-number-input';
import { usePublicAccountSettings } from '@/hooks/usePublicAccountSettings';
import {
  summarizePerson,
  type Person,
  type PersonUpdate,
} from '@littlebooker/dal';
import { apiPatchPublic } from '@/lib/api';
import {
  ARTIST_FIELD_LABELS,
  missingArtistFields,
  type InviteFormHandle,
  type InviteFormStatus,
} from '@/components/inviteRequirements';

type CustomSocialRow = { id: string; label: string; url: string };
type ArtistFormState = {
  name: string;
  pronouns: string;
  email: string;
  phone: string;
  bio: string;
  pictureUrl: string;
  imageFocus: string;
  instagram: string;
  soundcloud: string;
  bandcamp: string;
  customSocials: CustomSocialRow[];
};

type FieldErrors = Partial<Record<keyof ArtistFormState, string>>;

type BannerVariant = 'info' | 'success' | 'warning' | 'error';

type StatusBannerProps = {
  icon?: ReactNode;
  title: string;
  body: string;
  variant: BannerVariant;
};

const bannerStyles: Record<BannerVariant, string> = {
  info: 'bg-blue-50 text-blue-900 border-blue-100',
  success: 'bg-emerald-50 text-emerald-900 border-emerald-100',
  warning: 'bg-amber-50 text-amber-900 border-amber-100',
  error: 'bg-red-50 text-red-900 border-red-100',
};

const emptySocialRow = (): CustomSocialRow => ({
  id: Math.random().toString(36).slice(2, 9),
  label: '',
  url: '',
});

const valueOrNull = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const isValidUrl = (url: string): boolean => {
  if (!url || !url.trim()) return true; // Empty is valid (optional field)
  const trimmed = url.trim();
  // Check if it's just a username (starts with @ or has no protocol/domain)
  if (trimmed.startsWith('@') || (!trimmed.includes('.') && !trimmed.includes('/'))) {
    return false;
  }
  // Check if it looks like a URL (has protocol or domain)
  return trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.includes('.');
};

const mapPersonToFormState = (person: Person | null): ArtistFormState => {
  const customLinks = person?.socials?.custom ?? [];
  return {
    name: person?.name ?? '',
    pronouns: person?.pronouns ?? '',
    email: person?.email ?? '',
    phone: person?.phone ?? '',
    bio: person?.bio ?? '',
    pictureUrl: person?.picture_url ?? '',
    imageFocus: person?.image_focus ?? 'center',
    instagram: person?.socials?.instagram ?? '',
    soundcloud: person?.socials?.soundcloud ?? '',
    bandcamp: person?.socials?.bandcamp ?? '',
    customSocials:
      customLinks.length > 0
        ? customLinks.map((link) => ({ id: Math.random().toString(36).slice(2, 9), label: link.label, url: link.url }))
        : [emptySocialRow()],
  };
};

const buildSocialsPayload = (form: ArtistFormState): PersonUpdate['socials'] => {
  const custom = form.customSocials
    .filter((row) => row.label.trim() && row.url.trim())
    .map((row) => ({ label: row.label.trim(), url: row.url.trim() }));

  const socials = {
    instagram: valueOrNull(form.instagram),
    soundcloud: valueOrNull(form.soundcloud),
    bandcamp: valueOrNull(form.bandcamp),
    custom: custom.length > 0 ? custom : undefined,
  };

  if (!socials.instagram && !socials.soundcloud && !socials.bandcamp && !socials.custom) {
    return undefined;
  }
  return socials;
};

function StatusBanner({ icon, title, body, variant }: StatusBannerProps) {
  return (
    <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${bannerStyles[variant]}`}>
      <div className="mt-0.5 text-lg">{icon}</div>
      <div>
        <p className="font-semibold">{title}</p>
        <p className="text-sm/relaxed opacity-90">{body}</p>
      </div>
    </div>
  );
}

function ArtistDetails({ person }: { person: Person }) {
  const label = person.is_collective ? 'Collective' : 'Artist';
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-base font-semibold">{person.name || person.email}</p>
        <p className="text-sm text-muted-foreground">/{person.slug}</p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Contact</p>
        <p className="text-sm font-medium">{person.email}</p>
        {person.phone && <p className="text-sm text-muted-foreground">{person.phone}</p>}
      </div>
    </div>
  );
}

export interface ArtistProfileFormProps {
  person: Person;
  inviteToken: string;
  onSaveSuccess?: () => void;
  /**
   * Hide the form's own save button. The combined invite drives both forms from a
   * single submit instead, so that neither half can be sent on its own.
   */
  hideSubmitButton?: boolean;
  /** Lets a host page validate and save this form (see InviteFormHandle). */
  controlRef?: Ref<InviteFormHandle>;
  /** Fires whenever the outstanding required fields or the dirty flag change. */
  onStatusChange?: (status: InviteFormStatus) => void;
}

export function ArtistProfileForm({
  person,
  inviteToken,
  onSaveSuccess,
  hideSubmitButton = false,
  controlRef,
  onStatusChange,
}: ArtistProfileFormProps) {
  // Token-scoped account prefs — the anon invite page can't read the account row
  // via the DAL once RLS is on, so these come from the public API by invite token.
  const accountSettings = usePublicAccountSettings('artist', inviteToken);
  const defaultCountry = (accountSettings.default_country_code ?? 'NL') as CountryCode;
  const preferredImageRatio = accountSettings.preferred_image_ratio;
  const queryClient = useQueryClient();

  const [formState, setFormState] = useState<ArtistFormState>(() => mapPersonToFormState(person));
  const [formErrors, setFormErrors] = useState<FieldErrors>({});
  const [imageMismatched, setImageMismatched] = useState(false);
  const [imageIsSquare, setImageIsSquare] = useState(false);
  const [showMismatchDialog, setShowMismatchDialog] = useState(false);
  const [urlErrors, setUrlErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  // Dirty tracking, mirroring ShowDetailsForm: the combined invite turns its shared
  // save bar red on unsaved edits, and it needs both forms to report in.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const suppressDirtyRef = useRef(false);
  const isFirstRender = useRef(true);

  const summary = summarizePerson(person);
  const missingFields = summary?.missingFields ?? [];

  useEffect(() => {
    suppressDirtyRef.current = true;
    setFormState(mapPersonToFormState(person));
    setFormErrors({});
    setSubmitError(null);
    setHasUnsavedChanges(false);
  }, [person]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (suppressDirtyRef.current) {
      suppressDirtyRef.current = false;
      return;
    }
    setHasUnsavedChanges(true);
  }, [formState]);

  useEffect(() => {
    if (!showSavedMessage) {
      return undefined;
    }
    const timeout = setTimeout(() => setShowSavedMessage(false), 4000);
    return () => clearTimeout(timeout);
  }, [showSavedMessage]);

  const updateMutation = useMutation({
    mutationFn: (payload: PersonUpdate) =>
      apiPatchPublic<Person>('/artist', { token: inviteToken }, payload),
    onSuccess: (updatedPerson) => {
      queryClient.setQueryData(['person', person.slug], updatedPerson);
      suppressDirtyRef.current = true;
      setFormState(mapPersonToFormState(updatedPerson));
      setHasUnsavedChanges(false);
      setFormErrors({});
      setSubmitError(null);
      setShowSavedMessage(true);
      onSaveSuccess?.();
    },
    onError: (mutationError: unknown) => {
      if (mutationError instanceof Error) {
        setSubmitError(mutationError.message);
      } else {
        setSubmitError('Could not save your profile yet. Please try again.');
      }
    },
  });

  const handleFieldChange = (field: keyof ArtistFormState) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.target;
    setFormState((prev) => ({ ...prev, [field]: value }));

    // Validate social URLs
    if (['instagram', 'soundcloud', 'bandcamp'].includes(field)) {
      if (value && !isValidUrl(value)) {
        setUrlErrors((prev) => ({ ...prev, [field]: 'Please enter a full URL (e.g., https://instagram.com/yourname), not just a username' }));
      } else {
        setUrlErrors((prev) => ({ ...prev, [field]: '' }));
      }
    }
  };

  const handleCustomSocialChange = (id: string, field: 'label' | 'url', value: string) => {
    setFormState((prev) => ({
      ...prev,
      customSocials: prev.customSocials.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    }));

    // Validate custom social URLs
    if (field === 'url') {
      if (value && !isValidUrl(value)) {
        setUrlErrors((prev) => ({ ...prev, [`custom_${id}`]: 'Please enter a full URL (e.g., https://example.com), not just a username' }));
      } else {
        setUrlErrors((prev) => ({ ...prev, [`custom_${id}`]: '' }));
      }
    }
  };

  const addCustomSocialRow = () => {
    setFormState((prev) => ({ ...prev, customSocials: [...prev.customSocials, emptySocialRow()] }));
  };

  const removeCustomSocialRow = (id: string) => {
    setFormState((prev) => ({
      ...prev,
      customSocials:
        prev.customSocials.length === 1 ? prev.customSocials : prev.customSocials.filter((row) => row.id !== id),
    }));
  };

  const missingKeys = () => missingArtistFields(formState);

  const validateForm = (): FieldErrors => {
    const errors: FieldErrors = {};
    for (const key of missingKeys()) {
      errors[key] = 'This field is required';
    }
    return errors;
  };

  /** Shared by the form's own submit and by the imperative handle. */
  const submitForm = (): boolean => {
    if (!inviteToken) return false;
    setSubmitError(null);

    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return false;
    }

    const payload: PersonUpdate = {
      name: valueOrNull(formState.name),
      pronouns: valueOrNull(formState.pronouns),
      email: valueOrNull(formState.email) ?? undefined,
      phone: valueOrNull(formState.phone),
      bio: valueOrNull(formState.bio),
      picture_url: valueOrNull(formState.pictureUrl),
      image_focus: formState.imageFocus || null,
      socials: buildSocialsPayload(formState),
    };

    updateMutation.mutate(payload);
    return true;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitForm();
  };

  useEffect(() => {
    onStatusChange?.({
      missing: missingArtistFields(formState).map((key) => ARTIST_FIELD_LABELS[key]),
      dirty: hasUnsavedChanges,
    });
  }, [formState, hasUnsavedChanges, onStatusChange]);

  useImperativeHandle(controlRef, () => ({
    validate: () => {
      const missing = missingKeys();
      setFormErrors(validateForm());
      return missing.map((key) => ARTIST_FIELD_LABELS[key]);
    },
    save: () => submitForm(),
  }));

  return (
    <>
    <div className="space-y-6">
      <ArtistDetails person={person} />

      <div className="rounded-lg border bg-muted/30 p-4">
        <p className="text-sm font-semibold">Non-optional info left to fill in:</p>
        {missingFields.length > 0 ? (
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {missingFields.map((field) => (
              <li key={field}>{field}</li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            Looks like everything is already filled out. Feel free to review and make updates below.
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3">
            Basic Information
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {person?.is_collective ? 'Collective name' : 'Artist name'} <span className="text-destructive">*</span>
              </label>
              <input
                value={formState.name}
                onChange={handleFieldChange('name')}
                className="w-full rounded-md border border-input bg-background px-3 py-2"
                placeholder={person?.is_collective ? 'Your collective or crew name' : 'Your artist or project name'}
              />
              {formErrors.name && <p className="text-xs text-destructive">{formErrors.name}</p>}
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Pronouns</label>
              <input
                value={formState.pronouns}
                onChange={handleFieldChange('pronouns')}
                className="w-full rounded-md border border-input bg-background px-3 py-2"
                placeholder="she / they / he"
              />
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3">
            Contact
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">
                Email <span className="text-destructive">*</span>
              </label>
              <input
                type="email"
                value={formState.email}
                onChange={handleFieldChange('email')}
                className="w-full rounded-md border border-input bg-background px-3 py-2"
                placeholder="you@email.com"
              />
              {formErrors.email && <p className="text-xs text-destructive">{formErrors.email}</p>}
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">
                Phone <span className="text-destructive">*</span>
              </label>
              <PhoneInput
                value={formState.phone}
                onChange={(value) => setFormState(prev => ({ ...prev, phone: value }))}
                placeholder="Enter phone number"
                defaultCountry={defaultCountry}
                required
              />
              {formErrors.phone && <p className="text-xs text-destructive">{formErrors.phone}</p>}
            </div>
          </div>
        </div>

        {/* Photo & Bio Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3">
            Photo & Bio
          </h3>
          <div className="space-y-4">
            <div className="space-y-1">
              <PhotoUploadField
                label={person?.is_collective ? 'Collective photo *' : 'Artist photo *'}
                helper={<span className="font-bold text-red-600">Upload your press photo. {preferredImageRatio === 'portrait' ? 'Prefer portrait (3:4). Landscape photos crop in the center, so sides may be cut off.' : 'Prefer landscape (3:2). Portrait photos crop in the center, so heads may disappear.'}</span>}
                hideUrlInput
                value={formState.pictureUrl}
                onChange={(url) => setFormState(prev => ({ ...prev, pictureUrl: url }))}
                category="artist_photo"
                identifier={person.slug}
                uploadAuth={{ token: inviteToken, entityId: person.id }}
                imageFocus={formState.imageFocus}
                preferredRatio={preferredImageRatio}
                onOrientationMismatch={({ mismatched, isSquare }) => {
                  setImageMismatched(mismatched);
                  setImageIsSquare(isSquare);
                  if (mismatched) setShowMismatchDialog(true);
                }}
              />
              {formErrors.pictureUrl && <p className="text-xs text-destructive">{formErrors.pictureUrl}</p>}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">
                Bio <span className="text-destructive">*</span>
              </label>
              <textarea
                value={formState.bio}
                onChange={handleFieldChange('bio')}
                rows={4}
                className="w-full rounded-md border border-input bg-background px-3 py-2"
                placeholder="Short press-style bio, inspirations, recent highlights…"
              />
              <p className="text-xs text-muted-foreground">Give promoters and listeners a feel for your project.</p>
              {formErrors.bio && <p className="text-xs text-destructive">{formErrors.bio}</p>}
            </div>
          </div>
        </div>

        {/* Socials Section */}
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground border-l-4 border-primary pl-3">
              Socials
            </h3>
            <p className="text-xs text-muted-foreground mt-2 pl-4">Add anything you want us to highlight. Totally optional.</p>
          </div>

          <div className="space-y-1">
            <input
              value={formState.instagram}
              onChange={handleFieldChange('instagram')}
              className={`w-full rounded-md border ${urlErrors.instagram ? 'border-red-500' : 'border-input'} bg-background px-3 py-2`}
              placeholder="Instagram URL (e.g., https://instagram.com/yourname)"
            />
            {urlErrors.instagram && (
              <p className="text-xs text-red-600">{urlErrors.instagram}</p>
            )}
          </div>
          <div className="space-y-1">
            <input
              value={formState.soundcloud}
              onChange={handleFieldChange('soundcloud')}
              className={`w-full rounded-md border ${urlErrors.soundcloud ? 'border-red-500' : 'border-input'} bg-background px-3 py-2`}
              placeholder="SoundCloud URL (e.g., https://soundcloud.com/yourname)"
            />
            {urlErrors.soundcloud && (
              <p className="text-xs text-red-600">{urlErrors.soundcloud}</p>
            )}
          </div>

          <div className="space-y-1">
            <input
              value={formState.bandcamp}
              onChange={handleFieldChange('bandcamp')}
              className={`w-full rounded-md border ${urlErrors.bandcamp ? 'border-red-500' : 'border-input'} bg-background px-3 py-2`}
              placeholder="Bandcamp URL (e.g., https://yourname.bandcamp.com)"
            />
            {urlErrors.bandcamp && (
              <p className="text-xs text-red-600">{urlErrors.bandcamp}</p>
            )}
          </div>
          <div className="space-y-2 rounded-md border border-dashed border-input p-3">
            <p className="text-sm font-medium">Custom links</p>
            <div className="space-y-2">
              {formState.customSocials.map((social) => (
                <div key={social.id} className="grid gap-2 sm:grid-cols-[1fr_auto]">
                  <input
                    value={social.label}
                    onChange={(event) => handleCustomSocialChange(social.id, 'label', event.target.value)}
                    className="rounded-md border border-input bg-background px-3 py-2"
                    placeholder="Label (e.g. Mixcloud)"
                  />
                  <div className="flex gap-2">
                    <div className="flex-1 space-y-1">
                      <input
                        value={social.url}
                        onChange={(event) => handleCustomSocialChange(social.id, 'url', event.target.value)}
                        className={`w-full rounded-md border ${urlErrors[`custom_${social.id}`] ? 'border-red-500' : 'border-input'} bg-background px-3 py-2`}
                        placeholder="https://example.com"
                      />
                      {urlErrors[`custom_${social.id}`] && (
                        <p className="text-xs text-red-600">{urlErrors[`custom_${social.id}`]}</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeCustomSocialRow(social.id)}
                      className="shrink-0"
                    >
                      <span aria-hidden>×</span>
                      <span className="sr-only">Remove custom link</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addCustomSocialRow}>
              Add link
            </Button>
          </div>
        </div>

        {submitError && (
          <StatusBanner
            variant="error"
            title="Could not save yet"
            body={submitError}
            icon={<AlertTriangle className="h-5 w-5" />}
          />
        )}

        {showSavedMessage && (
          <StatusBanner
            variant="success"
            title="Profile saved"
            body="Thanks! You can revisit this link any time to make updates."
            icon={<CheckCircle2 className="h-5 w-5" />}
          />
        )}

        {!hideSubmitButton && (
          <Button type="submit" className="w-full md:w-auto" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving…' : 'Save profile'}
          </Button>
        )}
      </form>
    </div>

    {formState.pictureUrl && imageMismatched && (
      <OrientationMismatchDialog
        open={showMismatchDialog}
        onOpenChange={setShowMismatchDialog}
        imageUrl={formState.pictureUrl}
        preferredRatio={preferredImageRatio}
        imageFocus={formState.imageFocus}
        onImageFocusChange={(val) => setFormState(prev => ({ ...prev, imageFocus: val }))}
        isSquare={imageIsSquare}
      />
    )}
    </>
  );
}
